<template>
  <div
    v-if="visible"
    class="ws-cs-wrap"
    :class="[{ docked: state.dockedRight, dragging: state.dragging }, state.theme]"
    :style="wrapStyle"
    @mouseenter="state.isHover = true"
    @mouseleave="state.isHover = false"
  >
    <div v-if="!state.dockedRight" class="ws-cs-panel">
      <div class="ws-cs-header" @pointerdown.stop="onPointerDown">
        <div class="ws-cs-header-left" @pointerdown.stop>
          <el-button size="small" type="text" @click.stop="newChat" :title="$t('cs.newChat')">
            <i class="el-icon-plus"></i>
          </el-button>
        </div>
        <div class="ws-cs-title">{{ $t('cs.title') }}</div>
        <div class="ws-cs-actions" @pointerdown.stop>
          <el-button v-if="isLoggedIn" size="small" type="text" @click.stop="openSessionList" :title="$t('cs.chatHistory')">
            <i class="el-icon-chat-round"></i>
          </el-button>
          <el-button size="small" type="text" @click.stop="dockToRight">{{ $t('cs.collapse') }}</el-button>
        </div>
      </div>
      <!-- 会话列表覆盖层 -->
      <div v-if="showSessionList" class="ws-cs-session-overlay">
        <div class="ws-cs-session-header">
          <el-button size="small" type="text" @click="closeSessionList">
            <i class="el-icon-arrow-left"></i>
            {{ $t('cs.backToChat') }}
          </el-button>
          <span class="ws-cs-session-title">{{ $t('cs.chatHistory') }}</span>
        </div>
        <div class="ws-cs-session-list" @scroll="onSessionScroll">
          <div v-if="sessionListLoading && sessionList.length === 0" class="ws-cs-session-loading">
            <i class="el-icon-loading"></i>
            <span>{{ $t('cs.loadingHistory') }}</span>
          </div>
          <div v-else-if="!sessionListLoading && sessionList.length === 0" class="ws-cs-session-empty">
            {{ $t('cs.noHistory') }}
          </div>
          <template v-if="sessionList.length > 0">
            <div
              v-for="s in sessionList"
              :key="s.sessionId"
              class="ws-cs-session-item"
              :class="{ active: s.sessionId === sessionId }"
              @click="switchSession(s.sessionId)"
            >
              <div class="ws-cs-session-info">
                <div class="ws-cs-session-last">💬 {{ s.title || s.lastMessage || '...' }}</div>
                <div class="ws-cs-session-time">{{ s.lastMessageTime || s.createTime || '' }}</div>
              </div>
              <el-button size="mini" type="text" style="color: #F56C6C" @click.stop="deleteSession(s.sessionId)" :title="$t('cs.deleteChat')">
                <i class="el-icon-delete"></i>
              </el-button>
            </div>
            <div v-if="sessionListLoading" class="ws-cs-session-loadmore">
              <i class="el-icon-loading"></i>
            </div>
          </template>
        </div>
      </div>
      <div class="ws-cs-chat">
        <!-- 空状态欢迎语 -->
        <div v-if="messages.length === 0 && !showSessionList" class="ws-cs-welcome">
          <span v-if="displayGreeting">{{ displayGreeting }}</span>
        </div>
        <div v-else ref="scrollEl" class="ws-cs-list" @scroll="onScrollLoadMore">
          <div v-for="m in messages" :key="m.id" class="ws-cs-msg" :class="[m.role === 'user' ? 'from-user' : 'from-ai']">
            <template v-if="m.role === 'user'">
              <div class="ws-cs-bubble">
                <div v-if="m.imageUrl" class="ws-cs-user-img">
                  <el-image :src="m.imageUrl" fit="cover" lazy class="ws-cs-user-thumb" @click="openUrl(m.imageUrl)">
                    <div slot="error" class="image-slot-small"><i class="el-icon-picture-outline"></i></div>
                  </el-image>
                </div>
                <div v-if="m.content">{{ m.content }}</div>
              </div>
            </template>
            <template v-else>
              <div class="ws-cs-avatar-wrap">
                <el-avatar v-if="personaAvatarUrl" :size="32" class="ws-cs-avatar-inner" style="overflow:hidden;padding:0;">
                  <img :src="personaAvatarUrl" style="width:100%;height:100%;object-fit:cover;display:block;" />
                </el-avatar>
                <el-avatar v-else :size="32" class="ws-cs-avatar-inner">
                  <span style="font-size:14px;font-weight:600;color:#fff;">AI</span>
                </el-avatar>
              </div>
              <div class="ws-cs-ai-content">
                <!-- 等待 AI 输出时的提示（stopped后不再显示loading） -->
                <div v-if="!m.content && !m.stopped" class="ws-cs-bubble ws-cs-waiting">
                  <i class="el-icon-loading"></i>
                  <span>{{ $t('cs.loadingHistory') }}</span>
                </div>
                <div v-if="getParsed(m).text" class="ws-cs-bubble">
                  {{ getParsed(m).text }}
                </div>
                <!-- 流式 JSON 输出展示（思考模式） -->
                <div v-if="getParsed(m).streamingRaw" class="ws-cs-streaming-box" :class="{ stopped: m.stopped }">
                  <div v-if="m.stopped && !getParsed(m).text" class="ws-cs-streaming-stopped">{{ $t('cs.generationStopped') }}</div>
                  <pre class="ws-cs-streaming-pre">{{ getParsed(m).streamingRaw }}</pre>
                </div>
                <!-- Loading 占位符 -->
                <div v-if="getParsed(m).loadingLabel && !m.stopped" class="ws-cs-loading-placeholder">
                  <i class="el-icon-loading"></i>
                  <span>{{ getParsed(m).loadingLabel }}</span>
                </div>
                <!-- 商品推荐滑块 -->
                <div v-if="getParsed(m).products.length > 0" class="ws-cs-products-container">
                  <div class="ws-cs-products-scroll">
                    <div
                      v-for="p in getParsed(m).products"
                      :key="p.goodsUrl || p.goodsId || p.id"
                      class="ws-cs-product-card"
                      @click="toGoodsDetails(p)"
                    >
                      <div class="ws-cs-product-image">
                        <el-image :src="p.picPath" fit="cover" lazy>
                          <div slot="error" class="image-slot-small"><i class="el-icon-picture-outline"></i></div>
                        </el-image>
                      </div>
                      <div class="ws-cs-product-info">
                        <div class="ws-cs-product-name" :title="p.goodsName">{{ p.goodsName }}</div>
                        <div class="ws-cs-product-price">￥{{ p.goodsPrice }}</div>
                        <div class="ws-cs-product-meta">
                          <span class="platform-tag">{{ p.platform }}</span>
                          <span class="sold-count">{{ $t('cs.monthlySales') }} {{ p.monthSold }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="getParsed(m).orders.length > 0 || getParsed(m).containers.length > 0 || getParsed(m).containerOrders.length > 0 || getParsed(m).badOrders.length > 0 || getParsed(m).capitalRecords.length > 0" class="ws-cs-products-container">
                  <!-- 订单列表滑块卡片 -->
                  <div v-if="!getParsed(m).orderDetail" class="cs-logistics-slider">
                    <div class="cs-logistics-track">
                      <div
                        v-for="(o, oIdx) in getParsed(m).orders"
                        :key="o.id || oIdx"
                        class="cs-order-detail-card"
                        @click="toOrderDetail(o.orderId || o.id)"
                      >
                        <!-- 订单编号 + 状态 -->
                        <div class="cs-detail-id-status">
                          <span>No.{{ o.id || o.orderId }}</span>
                          <span class="cs-detail-status" :style="{ color: getOrderStatus(o.orderStatus).color }">
                            ● {{ getOrderStatus(o.orderStatus).label }}
                          </span>
                        </div>
                        <!-- 创建时间 -->
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.orderTime') }}:</span><span class="gv">{{ toDate(o.orderCreateTime || o.createTime) || '-' }}</span>
                        </div>
                        <!-- 负责人 -->
                        <div class="cs-list-info-row">
                          <span class="gl" style="color: #ff0000 !important;">{{ $t('cs.clientManagerUserName') }}:</span><span class="gv" style="color: #ff0000 !important;">{{ o.clientManagerUserName || '-' }}</span>
                        </div>
                        <!-- 商品区域 -->
                        <div class="cs-detail-goods">
                          <el-image :src="o.pictureUrl" fit="cover" class="cs-detail-thumb">
                            <div slot="error" class="image-slot-small"><i class="el-icon-picture-outline"></i></div>
                          </el-image>
                          <div class="cs-detail-goods-info">
                            <div class="cs-goods-name" :title="o.goodsName">{{ o.goodsName }}</div>
                            <div class="cs-goods-spec">
                              <span class="cs-spec-val">{{ o.colour || '-' }}</span>
                              <span class="cs-spec-val">{{ o.goodsSize || '-' }}</span>
                            </div>
                            <div class="cs-goods-price-row">
                              <span>{{ $t("cs.unitPrice") }}: <strong class="price-red">￥{{ formatPrice(o.price) }}</strong></span>
                              <span>{{ $t("cs.quantity") }}: <strong>{{ o.goodsCount }}</strong></span>
                            </div>
                          </div>
                        </div>
                        <!-- 国内物流信息 -->
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.trackingNo') }}:</span><span class="gv">{{ o.trackingNumberCn || '-' }}</span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.chinaFreight') }}:</span><span class="gv">{{ formatPrice(o.inlandFreight) }}{{ $t('cs.yuan') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- 批次列表滑块卡片 -->
                  <div v-if="getParsed(m).containers.length > 0" class="cs-container-slider">
                    <div class="cs-container-track" @scroll="onContainerScroll($event, m)">
                      <div
                        v-for="(c, cIdx) in getParsed(m).containers"
                        :key="c.id || cIdx"
                        class="cs-container-card"
                      >
                        <!-- 装箱编号 + 状态 -->
                        <div class="cs-detail-id-status">
                          <span>{{ c.containerBatchNo || '-' }}</span>
                          <span class="cs-detail-status" :style="{ color: getContainerState(c.containerState).color }">
                            ● {{ getContainerState(c.containerState).label }}
                          </span>
                        </div>
                        <!-- 信息行：用户ID -->
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.userId') }}:</span><span class="gv">{{ c.userName || '-' }}</span>
                        </div>
                        <!-- 信息行：创建日期 -->
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.createTime') }}:</span><span class="gv">{{ toDate(c.createTime) || '-' }}</span>
                        </div>
                        <!-- 信息行：发送日期 -->
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.sendTime') }}:</span><span class="gv">{{ toDate(c.sendTime) || '-' }}</span>
                        </div>
                        <!-- 详细信息网格 -->
                        <div class="cs-container-grid">
                          <div class="grid-item"><span class="gl">{{ $t('cs.boxesCount') }}:</span><span class="gv">{{ c.containerBoxesCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.route') }}:</span><span class="gv">{{ getRouteLabel(c.route) }}</span></div>
                          <div class="grid-item grid-full"><span class="gl">{{ $t('cs.logisticeCompany') }}:</span><span class="gv">{{ c.logisticeCompany || '-' }}</span></div>
                          <div class="grid-item grid-full"><span class="gl" style="color:#ff4d4f;">{{ $t('cs.senderName') }}:</span><span class="gv" style="color:#ff4d4f;">{{ c.senderName || '-' }}</span></div>
                          <div class="grid-item grid-full"><span class="gl" style="color:#ff4d4f;">{{ $t('cs.senderZipCode') }}:</span><span class="gv" style="color:#ff4d4f;">{{ c.senderZipCode || '-' }}</span></div>
                          <div class="grid-item grid-full"><span class="gl" style="color:#ff4d4f;">{{ $t('cs.senderAddress') }}:</span><span class="gv" style="color:#ff4d4f;">{{ [c.sendArea, c.senderAddress].filter(Boolean).join('') || '-' }}</span></div>
                          <div class="grid-item grid-full"><span class="gl" style="color:#1890ff;">{{ $t('cs.recipientName') }}:</span><span class="gv" style="color:#1890ff;">{{ c.recipientName || '-' }}</span></div>
                          <div class="grid-item grid-full"><span class="gl" style="color:#1890ff;">{{ $t('cs.zipCode') }}:</span><span class="gv" style="color:#1890ff;">{{ c.zipCode || '-' }}</span></div>
                          <div class="grid-item grid-full"><span class="gl" style="color:#1890ff;">{{ $t('cs.deliveryAddress') }}:</span><span class="gv" style="color:#1890ff;">{{ [c.addresserArea, c.deliveryAddress].filter(Boolean).join('') || '-' }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.goodsNumber') }}:</span><span class="gv">{{ c.goodsNumber ?? 0 }}</span></div>
                          <div class="grid-item grid-full"><span class="gl">{{ $t('cs.comments') }}:</span><span class="gv">{{ c.comments || '-' }}</span></div>
                        </div>
                      </div>
                    </div>
                    <div v-if="getParsed(m).containers.length > 1" class="cs-logistics-dots">
                      <span v-for="(dot, dIdx) in getParsed(m).containers" :key="'c-dot-' + dIdx"
                        class="cs-log-dot" :class="{ active: dIdx === 0 }"></span>
                    </div>
                  </div>
                  <!-- 装箱单列表滑块卡片 -->
                  <div v-if="getParsed(m).containerOrders.length > 0" class="cs-container-slider">
                    <div class="cs-container-track" @scroll="onContainerOrderScroll($event, m)">
                      <div
                        v-for="(co, coIdx) in getParsed(m).containerOrders"
                        :key="'co-' + (co.id || coIdx)"
                        class="cs-container-card"
                      >
                        <div class="cs-detail-id-status">
                          <span>{{ $t('cs.orderId') }}: {{ co.orderId || '-' }}</span>
                          <span class="cs-detail-status" :style="{ color: getOrderStatus(co.orderStatus).color }">
                            ● {{ getOrderStatus(co.orderStatus).label }}
                          </span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl" style="color: #ff0000 !important;">{{ $t('cs.clientManagerUserName') }}:</span><span class="gv" style="color: #ff0000 !important;">{{ co.clientManagerUserName || '-' }}</span>
                        </div>
                        <div class="cs-detail-goods">
                          <el-image :src="co.pictureUrl" fit="cover" class="cs-detail-thumb">
                            <div slot="error" class="image-slot-small"><i class="el-icon-picture-outline"></i></div>
                          </el-image>
                          <div class="cs-detail-goods-info">
                            <div class="cs-goods-name" :title="co.goodsName">{{ co.goodsName || '-' }}</div>
                            <div class="cs-goods-spec">
                              <span class="cs-spec-val">{{ co.colour || '-' }}</span>
                            </div>
                          </div>
                        </div>
                        <div class="cs-container-grid">
                          <div class="grid-item"><span class="gl">{{ $t('cs.goodsCount') }}:</span><span class="gv">{{ co.goodsCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.refundCount') }}:</span><span class="gv">{{ co.refundCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.destroyCount') }}:</span><span class="gv">{{ co.destroyCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.netWeight') }}:</span><span class="gv">{{ co.netWeight ?? '-' }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.canApplyCount') }}:</span><span class="gv">{{ co.canApplyCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.containerGoodsCount') }}:</span><span class="gv">{{ co.containerGoodsCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.containerGoodsCountInstalled') }}:</span><span class="gv">{{ co.containerGoodsCountInstalled ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.boxNum') }}:</span><span class="gv">{{ co.boxNum || '-' }}</span></div>
                        </div>
                      </div>
                    </div>
                    <div v-if="getParsed(m).containerOrders.length > 1" class="cs-logistics-dots">
                      <span v-for="(dot, dIdx) in getParsed(m).containerOrders" :key="'co-dot-' + dIdx"
                        class="cs-log-dot"></span>
                    </div>
                  </div>
                  <!-- 不良订单列表滑块卡片 -->
                  <div v-if="getParsed(m).badOrders.length > 0" class="cs-logistics-slider">
                    <div class="cs-logistics-track">
                      <div
                        v-for="(bo, boIdx) in getParsed(m).badOrders"
                        :key="'bo-' + (bo.id || boIdx)"
                        class="cs-order-detail-card"
                        @click="toDefectiveDetail(bo.id)"
                      >
                        <div class="cs-detail-id-status">
                          <span>No.{{ bo.id }}</span>
                          <span class="cs-detail-status" :style="{ color: getBadOrderStatus(bo.orderStatus).color }">
                            ● {{ getBadOrderStatus(bo.orderStatus).label }}
                          </span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.orderTime') }}:</span><span class="gv">{{ toDate(bo.createTime) || '-' }}</span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl" style="color: #ff0000 !important;">{{ $t('createOrder.createOrder102') }}:</span><span class="gv" style="color: #ff0000 !important;">{{ bo.userName || '-' }}</span>
                        </div>
                        <div class="cs-detail-goods">
                          <el-image :src="bo.pictureUrl" fit="cover" class="cs-detail-thumb">
                            <div slot="error" class="image-slot-small"><i class="el-icon-picture-outline"></i></div>
                          </el-image>
                          <div class="cs-detail-goods-info">
                            <div class="cs-goods-name" :title="bo.goodsName">{{ bo.goodsName }}</div>
                            <div class="cs-goods-spec">
                              <span class="cs-spec-val">{{ bo.colour || '-' }}</span>
                              <span class="cs-spec-val">{{ bo.goodsSize || '-' }}</span>
                            </div>
                            <div class="cs-goods-price-row">
                              <span>{{ $t("cs.unitPrice") }}: <strong class="price-red">￥{{ formatPrice(bo.price) }}</strong></span>
                              <span>{{ $t("cs.quantity") }}: <strong>{{ bo.goodsCount }}</strong></span>
                            </div>
                          </div>
                        </div>
                        <div class="cs-container-grid">
                          <div class="grid-item"><span class="gl">{{ $t('cs.goodsSku') }}:</span><span class="gv">{{ bo.goodsNumber || '-' }}</span></div>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.trackingNo') }}:</span><span class="gv">{{ bo.trackingNumberCn || '-' }}</span>
                          <span v-if="bo.cnLogisticsStatus" class="gv" style="color:#999;margin-left:4px;">({{ bo.cnLogisticsStatus }})</span>
                        </div>
                        <div class="cs-container-grid">
                          <div class="grid-item"><span class="gl">{{ $t('cs.chineseFreightFee') }}:</span><span class="gv">￥{{ formatPrice(bo.chineseFreightFee) }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.categoryFlag') }}:</span><span class="gv">{{ getCategoryFlag(bo.categoryFlag) }}</span></div>
                          <div class="grid-item" v-if="bo.putIntent"><span class="gl">{{ $t('cs.putIntent') }}:</span><span class="gv">{{ getPutIntentLabel(bo.putIntent) }}</span></div>
                        </div>
                        <div class="cs-container-grid">
                          <div class="grid-item"><span class="gl">{{ $t('cs.badCount') }}:</span><span class="gv" style="color:#ff4d4f;">{{ bo.badCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.exchangCount') }}:</span><span class="gv">{{ bo.exchangCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.reissueCount') }}:</span><span class="gv">{{ bo.reissueCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.refundCount') }}:</span><span class="gv">{{ bo.refundCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.destroyCount') }}:</span><span class="gv">{{ bo.destroyCount ?? 0 }}</span></div>
                          <div class="grid-item"><span class="gl">{{ $t('cs.waitBoxCount') }}:</span><span class="gv">{{ bo.waitBoxCount ?? 0 }}</span></div>
                        </div>
                        <div class="cs-list-info-row" v-if="bo.customerRemark">
                          <span class="gl">{{ $t('cs.customerRemark') }}:</span><span class="gv">{{ bo.customerRemark }}</span>
                        </div>
                        <div class="cs-list-info-row" v-if="bo.inspection">
                          <span class="gl">{{ $t('cs.inspection') }}:</span><span class="gv">{{ bo.inspection }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- 资金明细列表滑块卡片 -->
                  <div v-if="getParsed(m).capitalRecords.length > 0" class="cs-logistics-slider">
                    <div class="cs-logistics-track">
                      <div
                        v-for="(cr, crIdx) in getParsed(m).capitalRecords"
                        :key="'cr-' + (cr.tradingNo || crIdx)"
                        class="cs-order-detail-card"
                      >
                        <div class="cs-detail-id-status">
                          <span>{{ $t('cs.tradeNo') }}: {{ cr.tradingNo || '-' }}</span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.relationOrderNo') }}:</span><span class="gv">{{ cr.orderNo || '-' }}</span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.userId') }}:</span><span class="gv">{{ cr.userName || '-' }}</span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl" style="color: #ff0000 !important;">{{ $t('cs.clientManagerUserName') }}:</span><span class="gv" style="color: #ff0000 !important;">{{ cr.clientManagerUserName || '-' }}</span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.tradeIntro') }}:</span><span class="gv">{{ cr.dealDesc || '-' }}</span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.tradeTime') }}:</span><span class="gv">{{ toDate(cr.createTime) || '-' }}</span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.tradeType') }}:</span><span class="gv">{{ getCapitalTypeDetail(cr.typeDetail) }}</span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.amount') }}:</span><span class="gv" :style="{ color: cr.moneyFlag === '1' ? '#67C23A' : '#F56C6C' }">{{ getCapitalMoney(cr.moneyFlag, cr.money) }}{{ $t('cs.yuan') }}</span>
                        </div>
                        <div class="cs-list-info-row">
                          <span class="gl">{{ $t('cs.balance') }}:</span><span class="gv">{{ formatPrice(cr.balance) }}{{ $t('cs.yuan') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- 订单详情卡片 -->
                  <div v-if="getParsed(m).orderDetail" class="cs-detail-wrap">
                    <template v-for="(od, odIdx) in getParsed(m).orders">
                      <div :key="'od-id-' + (od.orderId || odIdx)" class="cs-detail-id-status">
                        <span>{{ $t('cs.orderId') }}: {{ od.orderId }}</span>
                        <span class="cs-detail-status" :style="{ color: getOrderStatus(od.orderStatus).color }">
                          ● {{ getOrderStatus(od.orderStatus).label }}
                        </span>
                      </div>
                      <div :key="'od-grid-' + (od.orderId || odIdx)" class="cs-detail-grid">
                        <div class="grid-item grid-two-third"><span class="gl">{{ $t('cs.orderTime') }}:</span><span class="gv">{{ toDate(od.orderCreateTime || od.createTime) }}</span></div>
                        <div class="grid-item"><span class="gl">{{ $t('cs.netWeight') }}:</span><span class="gv">{{ od.netWeight ?? 0 }}</span></div>
                        <div class="grid-item grid-full"><span class="gl">{{ $t('cs.receiveTime') }}:</span><span class="gv">{{ toDate(od.receiveTime) || '-' }}</span></div>
                        <div class="grid-item"><span class="gl">{{ $t('cs.badCount') }}:</span><span class="gv">{{ od.badCount ?? 0 }}</span></div>
                        <div class="grid-item"><span class="gl">{{ $t('cs.waitBoxCount') }}:</span><span class="gv">{{ od.waitBoxCount ?? 0 }}</span></div>
                        <div class="grid-item"><span class="gl">{{ $t('cs.exchangCount') }}:</span><span class="gv">{{ od.exchangCount ?? 0 }}</span></div>
                        <div class="grid-item"><span class="gl">{{ $t('cs.refundCount') }}:</span><span class="gv">{{ od.refundCount ?? 0 }}</span></div>
                        <div class="grid-item"><span class="gl">{{ $t('cs.reissueCount') }}:</span><span class="gv">{{ od.reissueCount ?? 0 }}</span></div>
                        <div class="grid-item"><span class="gl">{{ $t('cs.destroyCount') }}:</span><span class="gv">{{ od.destroyCount ?? 0 }}</span></div>
                        <div class="grid-item grid-full"><span class="gl">{{ $t('cs.sendTime') }}:</span><span class="gv">{{ toDate(od.sendTime) || '-' }}</span></div>
                      </div>
                      <!-- 商品区域 -->
                      <div :key="'od-goods-' + (od.orderId || odIdx)" class="cs-detail-goods">
                        <el-image :src="od.pictureUrl" fit="cover" lazy class="cs-detail-thumb">
                          <div slot="error" class="image-slot-small"><i class="el-icon-picture-outline"></i></div>
                        </el-image>
                        <div class="cs-detail-goods-info">
                          <div class="cs-goods-name" :title="od.goodsName">{{ od.goodsName }}</div>
                          <div class="cs-goods-spec">
                            <span class="cs-spec-val">{{ od.colour || '-' }}</span>
                            <span class="cs-spec-val">{{ od.goodsSize || '-' }}</span>
                          </div>
                          <div class="cs-goods-price-row">
                            <span>{{ $t("cs.unitPrice") }}: <strong class="price-red">￥{{ formatPrice(od.price) }}</strong></span>
                            <span>{{ $t("cs.quantity") }}: <strong>{{ od.goodsCount }}</strong></span>
                          </div>
                        </div>
                      </div>
                      <!-- 物流/地址信息 -->
                      <div :key="'od-info-' + (od.orderId || odIdx)" class="cs-detail-info-grid">
                        <div class="info-half">
                          <span class="il">{{ $t('cs.domesticTrackingNo') }}:</span><span class="iv">{{ od.trackingNumberCn || '-' }}</span>
                        </div>
                        <div class="info-half">
                          <span class="il">{{ $t('cs.chineseFreight') }}:</span><span class="iv">{{ formatPrice(od.chineseFreightFee) }}{{ $t('cs.yuan') }}</span>
                        </div>
                        <div class="info-full">
                          <span class="il">{{ $t('cs.customerRemark') }}:</span><span class="iv remark-text" v-if="od.customerRemark">{{ od.customerRemark }}</span><span class="iv" v-else>-</span>
                        </div>
                      </div>
                      <!-- 费用汇总表格 -->
                      <table :key="'od-table-' + (od.orderId || odIdx)" class="cs-detail-price-table">
                        <tr>
                          <td>{{ $t('cs.productPrice') }}</td><td>{{ formatPrice(od.goodsPrice) }}{{ $t('cs.yuan') }}</td>
                          <td>{{ $t('cs.agentServiceFee') }}</td><td>{{ formatPrice(od.agentServiceFee) }}{{ $t('cs.yuan') }}</td>
                        </tr>
                        <tr>
                          <td>{{ $t('cs.chineseFreight') }}</td><td>{{ formatPrice(od.chineseFreightFee) }}{{ $t('cs.yuan') }}</td>
                          <td>{{ $t('cs.inspectionFee') }}</td><td>{{ formatPrice(od.inspectionFee) }}{{ $t('cs.yuan') }}</td>
                        </tr>
                        <tr>
                          <td>{{ $t('cs.labelFee') }}</td><td>{{ formatPrice(od.labelFee) }}{{ $t('cs.yuan') }}</td>
                          <td>{{ $t('cs.problemHandlingFee') }}</td><td>{{ formatPrice(od.problemHandlingFee) }}{{ $t('cs.yuan') }}</td>
                        </tr>
                        <tr>
                          <td>{{ $t('cs.returnFee') }}</td><td>{{ formatPrice(od.returnFee) }}{{ $t('cs.yuan') }}</td>
                          <td>{{ $t('cs.categoryFee') }}</td><td>{{ formatPrice(od.categoryPrice) }}{{ $t('cs.yuan') }}</td>
                        </tr>
                        <tr>
                          <td>{{ $t('cs.exchangeInspectionFee') }}</td><td>{{ formatPrice(od.exchangeInspectionFee) }}{{ $t('cs.yuan') }}</td>
                          <td></td><td></td>
                        </tr>
                        <tr class="row-total">
                          <td colspan="3">{{ $t('cs.total') }}</td><td class="total-val">{{ sumPrice(od) }}{{ $t('cs.yuan') }}</td>
                        </tr>
                      </table>
                    </template>
                  </div>
                </div>
                <!-- 物流轨迹（左右滑动滑块） -->
                <div v-if="getParsed(m).logistics.length > 0" class="ws-cs-products-container">
                  <div v-for="(logGroup, gIdx) in getParsed(m).logistics" :key="'log-group-' + gIdx">
                    <div class="cs-log-table-slider">
                      <div class="cs-log-table-track" @scroll="onLogTableScroll($event, logGroup)" ref="logTableTrack">
                        <div v-for="(item, iIdx) in logGroup.items" :key="'log-item-' + iIdx" class="cs-log-table-card">
                          <table class="cs-log-table">
                            <tbody>
                              <tr>
                                <td class="cs-log-td-bg">{{ $t('logistics.logistics8') }}</td>
                                <td>{{ item.trackingNumber || '-' }}</td>
                                <td></td>
                              </tr>
                              <tr>
                                <td class="cs-log-td-bg">{{ $t('logistics.logistics6') }}</td>
                                <td class="cs-log-td-bg" colspan="2">
                                  <span class="cs-log-btn-green">{{ item.trackingInfoList && item.trackingInfoList.length > 0 ? item.trackingInfoList[0].trackingDetail : (item.logisticsStatus || '-') }}</span>
                                </td>
                              </tr>
                              <tr v-if="item.endFlag == '1'">
                                <td class="cs-log-td-bg">{{ $t('logistics.logistics4') }}</td>
                                <td colspan="2">
                                  <div>{{ $t('logistics.logistics16') }}</div>
                                  <div>
                                    {{ $t('logistics.logistics7') }}：<span class="cs-log-text-green">{{ item.receiveDate || '-' }}</span>
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td class="cs-log-td-bg">{{ $t('logistics.logistics9') }}</td>
                                <td colspan="2">{{ item.sendDate || '-' }}</td>
                              </tr>
                              <tr>
                                <td class="cs-log-td-bg">{{ $t('logistics.logistics10') }}</td>
                                <td colspan="2">{{ item.sendLocation || '-' }}</td>
                              </tr>
                              <tr>
                                <td class="cs-log-td-bg">{{ $t('logistics.logistics11') }}</td>
                                <td colspan="2">{{ item.receiveLocation || '-' }}</td>
                              </tr>
                              <tr v-if="item.trackingInfoList && item.trackingInfoList.length > 0">
                                <td class="cs-log-td-bg cs-log-col-status">{{ $t('logistics.logistics13') }}</td>
                                <td class="cs-log-td-bg cs-log-col-time">{{ $t('logistics.logistics14') }}</td>
                                <td class="cs-log-td-bg cs-log-col-location">{{ $t('logistics.logistics15') }}</td>
                              </tr>
                              <tr v-for="(track, tIdx) in item.trackingInfoList" :key="'track-' + tIdx">
                                <td class="cs-log-col-status">{{ track.trackingDetail }}</td>
                                <td class="cs-log-col-time">{{ track.checkpointDate }}</td>
                                <td class="cs-log-col-location" v-html="track.location">{{ track.location }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div v-if="logGroup.items.length > 1" class="cs-logistics-dots">
                        <span v-for="(dot, dIdx) in logGroup.items" :key="'dot-' + dIdx"
                          class="cs-log-dot" :class="{ active: dIdx === 0 }"
                          @click="scrollToLogTable(logGroup, dIdx)"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div class="ws-cs-input-area">
          <!-- 图片预览 -->
          <div v-if="pendingImage.url" class="ws-cs-pending-img">
            <el-image :src="pendingImage.url" fit="cover" class="pending-thumb" />
            <div class="pending-remove" @click="clearPendingImage">&times;</div>
          </div>
          <input
            ref="fileInput"
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            style="display:none"
            @change="onFileChange"
          />
          <div class="ws-cs-editor">
            <div class="ws-cs-toolbar">
              <button type="button" class="ws-cs-tool-btn" :disabled="sending" @click="triggerFileInput">
                <i class="el-icon-picture-outline"></i>
              </button>
              <div class="ws-cs-count">{{ inputText.length }} / 500</div>
            </div>
            <el-input
              v-model="inputText"
              type="textarea"
              resize="none"
              maxlength="500"
              :rows="4"
              :placeholder="$t('cs.inputTip')"
              :disabled="sending"
              class="ws-cs-textarea"
              ref="textareaRef"
              @keydown.enter.native.prevent="onSend"
            />
            <div class="ws-cs-editor-footer">
              <el-button
                v-if="sending"
                type="danger"
                plain
                @click="stopGenerating"
                class="ws-cs-stop-btn"
              >
                <i class="el-icon-close"></i>
                <span>{{ $t('cs.stopGenerate') }}</span>
              </el-button>
              <el-button v-else type="primary" @click="onSend" class="ws-cs-send-btn">{{ $t('cs.send') }}</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="ws-cs-ball" @pointerdown.stop="onPointerDown" @click.stop="expandToCenter">
      <img v-if="personaAvatarUrl" :src="personaAvatarUrl" class="ws-cs-ball-avatar" />
      <span v-else class="ws-cs-ball-text">{{ $t('cs.serviceShort') }}</span>
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n'
import Cookies from 'js-cookie'
import { dataTimeFn } from '@/assets/js/utils'

// 订单状态映射（与 filter.js 中的 orderStatusFilter 一致）
const ORDER_STATUS_MAP = [
  { value: '0', labelKey: 'cs.orderStatus0' },
  { value: '1', labelKey: 'cs.orderStatus1' },
  { value: '2', labelKey: 'cs.orderStatus2' },
  { value: '3', labelKey: 'cs.orderStatus3' },
  { value: '4', labelKey: 'cs.orderStatus4' },
  { value: '5', labelKey: 'cs.orderStatus5' },
  { value: '8', labelKey: 'cs.orderStatus8' },
  { value: '12', labelKey: 'cs.orderStatus12' },
  { value: '16', labelKey: 'cs.orderStatus16' },
  { value: '20', labelKey: 'cs.orderStatus20' },
  { value: '24', labelKey: 'cs.orderStatus24' },
  { value: '26', labelKey: 'cs.orderStatus26' },
  { value: '28', labelKey: 'cs.orderStatus28' },
  { value: '32', labelKey: 'cs.orderStatus32' },
  { value: '36', labelKey: 'cs.orderStatus36' },
  { value: '40', labelKey: 'cs.orderStatus40' },
  { value: '44', labelKey: 'cs.orderStatus44' },
  { value: '48', labelKey: 'cs.orderStatus48' },
  { value: '52', labelKey: 'cs.orderStatus52' },
  { value: '56', labelKey: 'cs.orderStatus56' },
  { value: '60', labelKey: 'cs.orderStatus60' },
  { value: '64', labelKey: 'cs.orderStatus64' },
  { value: '68', labelKey: 'cs.orderStatus68' },
  { value: '72', labelKey: 'cs.orderStatus72' },
  { value: '76', labelKey: 'cs.orderStatus76' },
  { value: '80', labelKey: 'cs.orderStatus80' },
  { value: '82', labelKey: 'cs.orderStatus82' },
  { value: '83', labelKey: 'cs.orderStatus83' },
  { value: '84', labelKey: 'cs.orderStatus84' },
  { value: '88', labelKey: 'cs.orderStatus88' },
  { value: '89', labelKey: 'cs.orderStatus89' },
]

const ORDER_STATUS_COLORS = '#E6A23C'

// 批次状态映射
const CONTAINER_STATE_MAP = [
  { value: '0', label: '装箱中', color: '#409EFF' },
  { value: '1', label: '待支付', color: '#E6A23C' },
  { value: '2', label: '待传发票', color: '#E6A23C' },
  { value: '3', label: '待发送', color: '#F56C6C' },
  { value: '4', label: '已发送', color: '#67C23A' },
  { value: '5', label: '已取消', color: '#909399' },
  { value: '6', label: '待装箱', color: '#909399' },
  { value: '7', label: '装箱完', color: '#67C23A' },
  { value: '8', label: '盘点完', color: '#67C23A' },
  { value: '9', label: '待发账单', color: '#E6A23C' },
  { value: '10', label: '可装箱', color: '#409EFF' }
]

const ROUTE_MAP = [
  { value: '1', label: i18n.t('cs.airTransport') },
  { value: '2', label: i18n.t('cs.seaTransport') }
]

const BAD_ORDER_STATUS_MAP = [
  { value: '24', label: '不良待处理', color: '#F56C6C' },
  { value: '26', label: '不良驳回', color: '#E6A23C' },
  { value: '28', label: '换货待发回', color: '#409EFF' },
  { value: '32', label: '换货等待到货', color: '#409EFF' },
  { value: '36', label: '待部分退货', color: '#E6A23C' },
  { value: '68', label: '待退货', color: '#E6A23C' },
  { value: '40', label: '补发等待到货', color: '#409EFF' },
  { value: '82', label: '待部分销毁', color: '#909399' }
]

const CATEGORY_FLAG_MAP = [
  { value: '0', label: '否' },
  { value: '1', label: '是' }
]

// AI API 基础路径（走网关 9999，/chat 代理到网关根路径）
const AI_API_BASE = '/api'
const LOCAL_PERSONA_CONFIG = {
  avatarUrl: '/ai-customer-service-avatar.png',
  greetingZh: '您好！我是 AI 客服小艾，请问有什么可以帮您？',
  greetingJa: 'こんにちは！AIカスタマーサービスの小艾です。何かお手伝いできますか？',
  greetingEn: 'Hello, I am AI customer service. How can I help you?'
}

function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0
    var v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function getViewportSize() {
  return {
    clientWidth: document.documentElement.clientWidth || window.innerWidth,
    clientHeight: document.documentElement.clientHeight || window.innerHeight
  }
}

function getResponsivePanelSize() {
  var _getViewportSize = getViewportSize()
  var clientWidth = _getViewportSize.clientWidth
  var clientHeight = _getViewportSize.clientHeight
  return {
    w: Math.min(500, Math.max(360, Math.round(clientWidth * 0.34))),
    h: Math.min(700, Math.max(520, Math.round(clientHeight * 0.78)))
  }
}

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v))
}

function formatAiContent(content) {
  var protocols = [
    { tag: '```json:products', key: 'products', label: i18n.t('cs.productInfo') },
    { tag: '```json:orders', key: 'orders', label: i18n.t('cs.orderInfo') },
    { tag: '```json:order_detail', key: 'order_detail', label: i18n.t('cs.orderDetails') },
    { tag: '```json:logistics', key: 'logistics', label: i18n.t('cs.logisticsInfo') },
    { tag: '```json:containers', key: 'containers', label: i18n.t('cs.containerInfo') },
    { tag: '```json:container_orders', key: 'container_orders', label: i18n.t('cs.containerOrderInfo') },
    { tag: '```json:bad_orders', key: 'bad_orders', label: i18n.t('cs.badOrderInfo') },
    { tag: '```json:capital_records', key: 'capital_records', label: i18n.t('cs.capitalRecordInfo') }
  ]

  var resultText = content
  var data = { products: [], orders: [], logistics: [], containers: [], containerOrders: [], badOrders: [], capitalRecords: [] }
  var orderDetail = null
  var loadingLabel = ''
  var streamingRaw = ''

  var cleanStreamingRaw = function (raw) {
    return raw.replace(/^\s+/, '').replace(/```\s*$/, '').trim()
  }

  // 1. 拦截并隐藏订单详细信息摘要
  var summaryStartTag = '**订单详细信息摘要：**'
  var summaryIndex = resultText.indexOf(summaryStartTag)
  if (summaryIndex !== -1) {
    var summaryEndIndex = resultText.length
    protocols.forEach(function (p) {
      var pIndex = resultText.indexOf(p.tag)
      if (pIndex > summaryIndex && pIndex < summaryEndIndex) {
        summaryEndIndex = pIndex
      }
    })
    var beforeSummary = resultText.substring(0, summaryIndex)
    var afterSummary = resultText.substring(summaryEndIndex)
    resultText = (beforeSummary.trim() + '\n' + afterSummary.trim()).trim()
  }

  // 2. 解析协议标签（支持同类型多个代码块）
  protocols.forEach(function (p) {
    while (true) {
      var startIndex = resultText.indexOf(p.tag)
      if (startIndex === -1) break
      var contentAfterStart = resultText.substring(startIndex + p.tag.length)
      var endIndex = contentAfterStart.indexOf('```')

      if (endIndex !== -1) {
        var jsonStr = contentAfterStart.substring(0, endIndex).trim()
        try {
          var parsed = JSON.parse(jsonStr)
          if (p.key === 'orders') {
            if (parsed && !Array.isArray(parsed) && parsed.list) {
              data.orders = data.orders.concat(Array.isArray(parsed.list) ? parsed.list : [])
            } else if (Array.isArray(parsed)) {
              data.orders = data.orders.concat(parsed)
            } else if (parsed && typeof parsed === 'object') {
              data.orders.push(parsed)
            }
          } else if (p.key === 'order_detail') {
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
              orderDetail = parsed
              data.orders = [parsed]
            }
          } else if (p.key === 'logistics') {
            if (Array.isArray(parsed)) {
              data.logistics = data.logistics.concat(parsed)
            } else if (parsed && typeof parsed === 'object') {
              data.logistics.push(parsed)
            }
          } else if (p.key === 'containers') {
            console.log('[containers] AI返回原始数据:', JSON.stringify(parsed, null, 2))
            if (Array.isArray(parsed)) {
              data.containers = data.containers.concat(parsed)
            } else if (parsed && typeof parsed === 'object') {
              if (parsed.list && Array.isArray(parsed.list)) {
                data.containers = data.containers.concat(parsed.list)
              } else {
                data.containers.push(parsed)
              }
            }
          } else if (p.key === 'container_orders') {
            console.log('[container_orders] AI返回原始数据:', JSON.stringify(parsed, null, 2))
            if (Array.isArray(parsed)) {
              data.containerOrders = data.containerOrders.concat(parsed)
            } else if (parsed && typeof parsed === 'object') {
              if (parsed.list && Array.isArray(parsed.list)) {
                data.containerOrders = data.containerOrders.concat(parsed.list)
              } else {
                data.containerOrders.push(parsed)
              }
            }
          } else if (p.key === 'bad_orders') {
            console.log('[bad_orders] AI返回原始数据:', JSON.stringify(parsed, null, 2))
            if (Array.isArray(parsed)) {
              data.badOrders = data.badOrders.concat(parsed)
            } else if (parsed && typeof parsed === 'object') {
              if (parsed.list && Array.isArray(parsed.list)) {
                data.badOrders = data.badOrders.concat(parsed.list)
              } else {
                data.badOrders.push(parsed)
              }
            }
          } else if (p.key === 'products') {
            if (Array.isArray(parsed)) {
              data.products = data.products.concat(parsed)
            } else if (parsed && typeof parsed === 'object') {
              if (parsed.list && Array.isArray(parsed.list)) {
                data.products = data.products.concat(parsed.list)
              } else {
                data.products.push(parsed)
              }
            }
          } else if (p.key === 'capital_records') {
            if (Array.isArray(parsed)) {
              data.capitalRecords = data.capitalRecords.concat(parsed)
            } else if (parsed && typeof parsed === 'object') {
              if (parsed.list && Array.isArray(parsed.list)) {
                data.capitalRecords = data.capitalRecords.concat(parsed.list)
              } else {
                data.capitalRecords.push(parsed)
              }
            }
          }
        } catch (e) {
          loadingLabel = i18n.t('cs.querying', { label: p.label })
        }
        var before = resultText.substring(0, startIndex)
        var after = contentAfterStart.substring(endIndex + 3)
        resultText = (before + '\n' + after).trim()
      } else {
        loadingLabel = i18n.t('cs.querying', { label: p.label })
        streamingRaw = cleanStreamingRaw(contentAfterStart)
        resultText = resultText.substring(0, startIndex).trim()
        break
      }
    }
  })

  // 物流数据后处理：扁平数组 → 合并到一组（滑块展示，左右滑动）
  if (data.logistics.length > 0) {
    var logGroupMap = {}
    data.logistics.forEach(function (log) {
      // 所有同次查询的物流数据合并到同一个组，共用滑块
      var targetKey = 'lg_0'
      if (!logGroupMap[targetKey]) {
        logGroupMap[targetKey] = { queryLabel: null, items: [] }
      }
      // 新数据结构：trackingInfoList [{ checkpointDate, trackingDetail, location }]
      var trackingInfoList = Array.isArray(log.trackingInfoList) ? log.trackingInfoList : []
      // 兼容旧数据结构（logisticsSteps）
      if (trackingInfoList.length === 0 && Array.isArray(log.logisticsSteps)) {
        trackingInfoList = log.logisticsSteps.map(function (s) {
          return {
            checkpointDate: s.time || '',
            trackingDetail: s.description || '',
            location: ''
          }
        })
      }
      // 兜底：location 为空时，从 trackingDetail 中解析地点（格式："动作 - 地点" 或 "动作・地点"）
      trackingInfoList.forEach(function (t) {
        if (!t.location && t.trackingDetail) {
          var sepIdx = -1
          // 优先匹配 " - "（空格-空格），其次匹配 "・"
          if (t.trackingDetail.indexOf(' - ') !== -1) {
            sepIdx = t.trackingDetail.lastIndexOf(' - ')
          } else if (t.trackingDetail.indexOf('・') !== -1) {
            sepIdx = t.trackingDetail.lastIndexOf('・')
          } else if (t.trackingDetail.indexOf('-') !== -1) {
            sepIdx = t.trackingDetail.lastIndexOf('-')
          }
          if (sepIdx !== -1) {
            var loc = t.trackingDetail.substring(sepIdx + (t.trackingDetail.charAt(sepIdx + 1) === ' ' ? 2 : 1)).trim()
            // 去掉地点前残留的 - 或 ・
            if (loc.indexOf('-') === 0 || loc.indexOf('・') === 0) {
              loc = loc.substring(1).trim()
            }
            t.location = loc
            t.trackingDetail = t.trackingDetail.substring(0, sepIdx).trim()
          }
        }
      })
      logGroupMap[targetKey].items.push({
        trackingNumber: log.trackingNumber || '',
        containerBatchNo: log.containerBatchNo || '',
        logisticsStatus: log.logisticsStatus || '',
        trackingInfoList: trackingInfoList,
        sendDate: log.sendDate || (trackingInfoList.length > 0 ? trackingInfoList[trackingInfoList.length - 1].checkpointDate : ''),
        sendLocation: log.sendLocation || (trackingInfoList.length > 0 ? trackingInfoList[trackingInfoList.length - 1].location : ''),
        receiveLocation: log.receiveLocation || (trackingInfoList.length > 0 ? trackingInfoList[0].location : ''),
        receiveDate: log.receiveDate || '',
        endFlag: log.endFlag || ''
      })
    })
    data.logistics = Object.values(logGroupMap)
  }

  return {
    text: resultText,
    loadingLabel: loadingLabel,
    streamingRaw: streamingRaw,
    orderDetail: orderDetail,
    products: data.products,
    orders: data.orders,
    logistics: data.logistics,
    containers: data.containers,
    containerOrders: data.containerOrders,
    badOrders: data.badOrders,
    capitalRecords: data.capitalRecords
  }
}

export default {
  name: 'WsCustomerService',
  data: function () {
    var initialPanelSize = getResponsivePanelSize()
    var viewport = getViewportSize()
    return {
      visible: true,
      state: {
        x: viewport.clientWidth - 44,
        y: Math.max(100, Math.min(viewport.clientHeight - 56 - 20, viewport.clientHeight * 0.5 - 28)),
        w: initialPanelSize.w,
        h: initialPanelSize.h,
        size: 56,
        dragging: false,
        dx: 0,
        dy: 0,
        dockedRight: true,
        isHover: false,
        theme: 'ws-cs-theme-default'
      },
      messages: [],
      inputText: '',
      pendingImage: { url: '', imageId: '', ossUrl: '' },
      sending: false,
      activeStreamController: null,
      sessionId: '',
      messagesLoaded: false,
      showSessionList: false,
      sessionList: [],
      sessionListLoading: false,
      lastToken: '',
      personaConfig: LOCAL_PERSONA_CONFIG
    }
  },
  computed: {
    isLoggedIn: function () {
      return !!window.sessionStorage.getItem('token')
    },
    personaAvatarUrl: function () {
      var url = this.personaConfig && this.personaConfig.avatarUrl || ''
      if (!url) return ''
      if (url.startsWith('http')) return url
      return (process.env.VUE_APP_BASE_IMG_HEADER || '') + url
    },
    displayGreeting: function () {
      if (!this.personaConfig) return this.$t('cs.welcome')
      var lang = this.getLang()
      var langMap = { 'zh-CN': 'greetingZh', 'ja-JP': 'greetingJa', 'en-US': 'greetingEn' }
      var key = langMap[lang]
      if (key && this.personaConfig[key]) return this.personaConfig[key]
      // 兜底：greeting 字段
      if (this.personaConfig.greeting) return this.personaConfig.greeting
      return ''
    },
    wrapStyle: function () {
      var width = this.state.dockedRight ? this.state.size + 'px' : this.state.w + 'px'
      var height = this.state.dockedRight ? this.state.size + 'px' : this.state.h + 'px'
      var style = {
        left: this.state.x + 'px',
        top: this.state.y + 'px',
        width: width,
        height: height,
        maxWidth: 'calc(100vw - 20px)',
        maxHeight: 'calc(100vh - 20px)'
      }
      if (this.state.dockedRight && !this.state.dragging) {
        if (this.state.isHover) {
          style.transform = 'translateX(-22px)'
        } else {
          style.transform = 'translateX(0)'
        }
      }
      return style
    }
  },
  watch: {
    isLoggedIn: function (loggedIn) {
      var currentToken = window.sessionStorage.getItem('token') || ''
      if (currentToken !== this.lastToken) {
        this.lastToken = currentToken
        this.messages = []
        this.sessionId = ''
        this.showSessionList = false
        this.sessionList = []
        this.messagesLoaded = false
      }
    }
  },
  mounted: function () {
    this.onResize()
    window.addEventListener('resize', this.onResize)
    document.addEventListener('pointerdown', this.onDocumentPointerDown)
    this.lastToken = window.sessionStorage.getItem('token') || ''
    this.fetchPersonaConfig()
    this.fetchAiEnabled()
  },
  beforeDestroy: function () {
    if (this.activeStreamController) {
      this.activeStreamController.abort()
    }
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('pointerdown', this.onDocumentPointerDown)
  },
  methods: {
    onDocumentPointerDown: function (e) {
      if (this.state.dockedRight) return
      if (this.state.dragging) return
      var el = this.$el
      if (el && el.contains(e.target)) return
      this.dockToRight()
    },
    // === 人设配置 ===
    fetchPersonaConfig: function () {
      var self = this
      var tenantId = window.sessionStorage.getItem('tenant-id') || '1'
      this.$http.get('aiTenantConfig/ai-persona', {
        params: { tenantId: tenantId }
      }).then(function (res) {
        var result = res.data
        var cfg = (result && result.data && result.data.personaConfig) || null
        if (cfg) {
          self.personaConfig = {
            avatarUrl: cfg.avatarUrl || LOCAL_PERSONA_CONFIG.avatarUrl,
            greetingZh: cfg.greetingZh || LOCAL_PERSONA_CONFIG.greetingZh,
            greetingJa: cfg.greetingJa || LOCAL_PERSONA_CONFIG.greetingJa,
            greetingEn: cfg.greetingEn || LOCAL_PERSONA_CONFIG.greetingEn
          }
        } else {
          self.personaConfig = LOCAL_PERSONA_CONFIG
        }
      }).catch(function () {
        self.personaConfig = LOCAL_PERSONA_CONFIG
      })
    },

    // === 检查是否开启AI客服 ===
    fetchAiEnabled: function () {
      var self = this
      this.$http.get('customer/getCustomerInfo').then(function (res) {
        var data = res.data
        if (data && data.data && data.data.aiChatEnabled === '0') {
          self.visible = false
        }
      }).catch(function () {
        // 接口异常时默认显示，不阻塞用户
      })
    },

    // === 工具方法 ===
    getUserId: function () {
      try {
        var userInfo = JSON.parse(window.sessionStorage.getItem('userInfo') || '{}')
        var userId = userInfo.userId || userInfo.id || userInfo.userName || userInfo.username || ''
        if (userId) return userId
      } catch (e) {}
      if (window.sessionStorage.getItem('token')) {
        return 'user_' + Date.now()
      }
      var STORAGE_KEY = 'ws_cs_anonymous_id'
      var anonymousId = localStorage.getItem(STORAGE_KEY)
      if (!anonymousId) {
        anonymousId = String(Date.now())
        localStorage.setItem(STORAGE_KEY, anonymousId)
      }
      return anonymousId
    },
    getLang: function () {
      return Cookies.get('language') || 'ja-JP'
    },
    _getCommonHeaders: function () {
      var headers = {
        'Accept-Language': this.getLang(),
        'ClientId': 'customer-app',
        'CLIENT-TOC': 'Y',
        'tenant-id': window.sessionStorage.getItem('tenant-id') || '1'
      }
      var token = window.sessionStorage.getItem('token')
      if (token) headers['Authorization'] = token
      return headers
    },
    toDate: function (val) {
      return dataTimeFn(val)
    },
    formatPrice: function (val) {
      if (val == null || val === '') return '0'
      var s = Number(val).toFixed(4)
      // 去掉末尾多余的0 和多余的小数点: 1.5500→1.55, 1.5000→1.5
      return s.replace(/\.?0+$/, '')
    },
    sumPrice: function (od) {
      var a =
        Number(od && od.goodsPrice || 0) +
        Number(od && od.agentServiceFee || 0) +
        Number(od && od.chineseFreightFee || 0) +
        Number(od && od.inspectionFee || 0) +
        Number(od && od.labelFee || 0) +
        Number(od && od.problemHandlingFee || 0) +
        Number(od && od.returnFee || 0) +
        Number(od && od.categoryPrice || 0)
      return this.formatPrice(a)
    },
    getOrderStatus: function (status) {
      var s = String(status)
      var found = ORDER_STATUS_MAP.find(function (item) { return item.value === s })
      if (found) {
        return { label: i18n.t(found.labelKey), color: ORDER_STATUS_COLORS }
      }
      return { label: i18n.t('cs.unknown'), color: '#999' }
    },
    getContainerState: function (state) {
      var s = String(state)
      var found = CONTAINER_STATE_MAP.find(function (item) { return item.value === s })
      if (found) {
        return { label: found.label, color: found.color }
      }
      return { label: i18n.t('cs.unknown'), color: '#999' }
    },
    getRouteLabel: function (route) {
      var r = String(route)
      var found = ROUTE_MAP.find(function (item) { return item.value === r })
      return found ? found.label : (r || '-')
    },
    getBadOrderStatus: function (status) {
      var s = String(status)
      var found = BAD_ORDER_STATUS_MAP.find(function (item) { return item.value === s })
      if (found) {
        return { label: found.label, color: found.color }
      }
      return { label: i18n.t('cs.unknown'), color: '#999' }
    },
    getCategoryFlag: function (flag) {
      var f = String(flag)
      var found = CATEGORY_FLAG_MAP.find(function (item) { return item.value === f })
      return found ? found.label : (f || '-')
    },
    getOrderTypeLabel: function (type) {
      var t = String(type)
      if (t === '0') return 'FBA'
      if (t === '1') return 'YFF'
      if (t === '2') return 'ZOZO'
      if (t === '3') return 'RSL'
      return t || '-'
    },
    getPutIntentLabel: function (intent) {
      var v = String(intent)
      if (v === '0') return '无'
      if (v === '1') return '纸箱'
      if (v === '2') return '托盘'
      return v || '-'
    },
    getCapitalTypeDetail: function (status) {
      var s = String(status)
      var map = {
        '1': 'typeDetail1',
        '2': 'typeDetail2',
        '2+': 'typeDetail2plus',
        '3': 'typeDetail3',
        '4': 'typeDetail4',
        '5': 'typeDetail5',
        '6': 'typeDetail6',
        '7': 'typeDetail7',
        '8': 'typeDetail8',
        '9': 'typeDetail9',
        '10': 'typeDetail10',
        '11': 'typeDetail11',
        '12': 'typeDetail12',
        '13': 'typeDetail13',
        '14': 'typeDetail14',
        '15': 'typeDetail15',
        '16': 'typeDetail16',
        '16+': 'typeDetail16plus'
      }
      var key = map[s]
      if (key) return this.$t('cs.' + key)
      return s || '-'
    },
    getCapitalMoney: function (moneyFlag, money) {
      if (money == null || money === '') return '0'
      var prefix = moneyFlag === '1' ? '+' : '-'
      return prefix + this.formatPrice(money)
    },
    getParsed: function (m) {
      if (m.parsed) return m.parsed
      return formatAiContent(m.content || '')
    },

    // === 路由跳转 ===
    toOrderDetail: function (id) {
      this.$router.push({ name: 'orderDetail', query: { id: id, isEdit: '0' } })
    },
    toDefectiveDetail: function (id) {
      this.$router.push({ name: 'defective', query: { orderid: id } })
    },
    openUrl: function (url) {
      if (url) window.open(url, '_blank')
    },
    triggerFileInput: function () {
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    clearPendingImage: function () {
      this.pendingImage.url = ''
      this.pendingImage.imageId = ''
      this.pendingImage.ossUrl = ''
    },
    onFileChange: function (e) {
      var self = this
      var target = e.target
      var file = target.files && target.files[0]
      if (!file) return
      target.value = ''

      // 本地预览
      var reader = new FileReader()
      reader.onload = function (ev) {
        self.pendingImage.url = ev.target && ev.target.result || ''
      }
      reader.readAsDataURL(file)

      // 上传到1688拿 imageId（图搜用）
      var formData1688 = new FormData()
      formData1688.append('file', file)
      formData1688.append('modular', 'shopmall')

      var headers1688 = this._getCommonHeaders()
      delete headers1688['Content-Type'] // 让浏览器自动设置 multipart/form-data
      fetch(process.env.VUE_APP_BASE_API + '/shop/1688/goods/uploadByAll', {
        method: 'POST',
        headers: headers1688,
        body: formData1688
      }).then(function (res) {
        return res.json()
      }).then(function (result) {
        if (result.code === 200 && result.data) {
          // data['1688'] 可能是字符串(imageId)也可能是对象(含.result/.picPath)
          var img1688 = result.data['1688']
          var imageId = (typeof img1688 === 'object' && img1688 !== null) ? (img1688.result || img1688.id || '') : (img1688 || '')
          self.pendingImage.imageId = imageId
        }
      }).catch(function (err) {
        console.error('1688 upload image error:', err)
      })

      // 上传到OSS拿短URL（历史消息展示用，只存短URL不存base64）
      var formDataOss = new FormData()
      formDataOss.append('file', file)
      formDataOss.append('modular', 'chat')
      formDataOss.append('type', 'JPG')

      var headersOss = this._getCommonHeaders()
      delete headersOss['Content-Type']
      fetch(process.env.VUE_APP_BASE_API + '/aliyun/oss/upload', {
        method: 'POST',
        headers: headersOss,
        body: formDataOss
      }).then(function (res) {
        return res.json()
      }).then(function (ossResult) {
        if (ossResult.code === 200 && ossResult.data) {
          self.pendingImage.ossUrl = ossResult.data
        }
      }).catch(function (err) {
        console.error('OSS upload image error:', err)
      })
    },
    toGoodsDetails: function (p) {
      if (p.goodsId || p.id) {
        this.$router.push({
          path: '/urlGoods/details',
          query: { id: p.goodsId || p.id, platform: p.platform || 'aoimart', numId: p.numId }
        })
      } else if (p.goodsUrl) {
        window.open(p.goodsUrl)
      }
    },
    // === 会话管理 ===
    newChat: function () {
      this.sessionId = ''
      this.messages = []
      this.showSessionList = false
      this.messagesLoaded = false
    },
    openSessionList: function () {
      this.showSessionList = true
      this.sessionList = []
      this.loadSessions()
    },
    closeSessionList: function () {
      this.showSessionList = false
    },

    // === 会话列表加载 ===
    loadSessions: function () {
      var self = this
      if (this.sessionListLoading) return
      this.sessionListLoading = true

      var headers = this._getCommonHeaders()
      fetch(AI_API_BASE + '/chat/sessions', {
        method: 'GET',
        headers: headers
      })
        .then(function (res) { return res.json() })
        .then(function (result) {
          // 接口返回 { code: 0, data: [...] }
          var list = Array.isArray(result && result.data) ? result.data : []
          // 适配字段：后端 id → sessionId, title → title, createTime → createTime
          self.sessionList = list.map(function (item) {
            return {
              sessionId: item.id || item.sessionId,
              title: item.title || item.lastMessage || '',
              lastMessageTime: item.createTime || '',
              createTime: item.createTime || ''
            }
          })
        })
        .catch(function (e) {
          console.error('Load sessions error:', e)
        })
        .finally(function () {
          self.sessionListLoading = false
        })
    },
    onSessionScroll: function () {
      // 新接口无分页，无需滚动加载
    },
    deleteSession: function (sid) {
      var self = this
      this.$confirm(i18n.t('cs.deleteChatConfirm'), i18n.t('cs.deleteChat'), {
        confirmButtonText: i18n.t('cs.deleteChat'),
        cancelButtonText: i18n.t('cs.backToChat'),
        type: 'warning'
      }).then(function () {
        // 新接口无删除 API，仅从本地列表移除
        self.sessionList = self.sessionList.filter(function (s) { return s.sessionId !== sid })
        if (sid === self.sessionId) {
          self.messages = []
          self.sessionId = ''
        }
      }).catch(function () {})
    },
    switchSession: function (sid) {
      this.sessionId = sid
      this.showSessionList = false
      this.loadSessionMessages(sid)
    },

    // === 历史消息加载 ===
    loadSessionMessages: function (sid) {
      var self = this
      this.messages = []

      fetch(AI_API_BASE + '/chat/messages/' + sid, {
        method: 'GET',
        headers: this._getCommonHeaders()
      })
        .then(function (res) { return res.json() })
        .then(function (result) {
          // 接口返回 { code: 0, data: [{id, role, content, createTime}, ...] }
          var list = Array.isArray(result && result.data) ? result.data : []
          self.messages = list.map(function (item) {
            return {
              id: String(item.id),
              role: item.role || 'assistant',
              content: item.content || '',
              imageUrl: item.imageUrl || '',
              stopped: true
            }
          })
          self.messagesLoaded = true
          self.$nextTick(self.autoScroll)
        })
        .catch(function (e) {
          console.error('Load session messages error:', e)
          self.messagesLoaded = true
        })
    },
    onScrollLoadMore: function () {
      // 新接口无分页，无需滚动加载
    },

    // === AI 流式对话 ===
    callAiStream: function (message, onChunk, signal, imageId, imageUrl) {
      var self = this
      var body = { message: message }
      if (imageId) {
        body.imageId = imageId
      }
      if (imageUrl) {
        body.imageUrl = imageUrl
      }
      // 继续已有会话时传 sessionId，新建不传
      if (this.sessionId) {
        body.sessionId = this.sessionId
      }

      return fetch(AI_API_BASE + '/chat/conversation', {
        method: 'POST',
        headers: Object.assign({
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        }, self._getCommonHeaders()),
        body: JSON.stringify(body),
        signal: signal
      }).then(function (response) {
        if (!response.ok || !response.body) {
          return response.text().catch(function () { return i18n.t('cs.networkError') }).then(function (errorText) {
            throw new Error(errorText || ('HTTP ' + response.status))
          })
        }
        // 从 SSE 事件中提取 sessionId（如果后端返回）
        var sseSessionId = response.headers.get('X-Session-Id')
        if (sseSessionId && !self.sessionId) {
          self.sessionId = sseSessionId
        }

        var reader = response.body.getReader()
        var decoder = new TextDecoder()
        var buffer = ''

        function read() {
          return reader.read().then(function (result) {
            if (result.done) return
            buffer += decoder.decode(result.value, { stream: true })
            var lines = buffer.split('\n')
            buffer = lines.pop() || ''
            for (var i = 0; i < lines.length; i++) {
              var line = lines[i]
              var trimmed = line.trim()
              if (!trimmed || !trimmed.startsWith('data:')) continue

              var content = line.slice(line.indexOf('data:') + 5)
              if (content.trim() === '[DONE]') break

              try {
                var trimmedContent = content.trim()
                if (trimmedContent.startsWith('{')) {
                  var json = JSON.parse(trimmedContent)
                  // 新格式（Spring AI ChatResponse）: json.result.output.content
                  // 旧格式（pig-mesh DeepSeek）: json.choices[0].delta.content
                  var text = (json.result && json.result.output && (json.result.output.content || json.result.output.text))
                    || (json.choices && json.choices[0] && json.choices[0].delta && json.choices[0].delta.content)
                    || json.content
                    || json.text
                    || ''
                  if (text) onChunk(text)
                } else {
                  onChunk(content)
                }
              } catch (e) {
                onChunk(content)
              }
            }
            return read()
          })
        }
        return read()
      })
    },
    stopGenerating: function () {
      var self = this
      if (this.sessionId) {
        var headers = this._getCommonHeaders()
        fetch(AI_API_BASE + '/chat/stop/' + this.sessionId, { method: 'POST', headers: headers })
          .then(function (res) {
            if (!res.ok) {
              // 停止接口返回失败（如session不存在），说明AI已自动回复完成，直接结束UI状态
              self._finishGenerating()
              return { _handled: true }
            }
            return res.json().catch(function () { return {} })
          })
          .then(function (result) {
            if (result && result._handled) return
            // 停止接口调用成功（HTTP 200），立即更新UI状态，不等SSE关闭
            self._finishGenerating()
          })
          .catch(function () {
            // 网络错误等，也视为已完成（session可能已被清理）
            self._finishGenerating()
          })
      } else {
        // 没有 sessionId 时回退到 abort
        if (this.activeStreamController) {
          this.activeStreamController.abort()
        }
      }
    },
    _finishGenerating: function () {
      // 将UI状态改为"已完成"：隐藏停止按钮、关闭loading、展示已收到的回复内容
      var lastMsg = this.messages.length > 0 ? this.messages[this.messages.length - 1] : null
      if (lastMsg && lastMsg.role === 'assistant') {
        lastMsg.stopped = true
        if (!lastMsg.content) {
          lastMsg.content = ''
        }
        this.$set(lastMsg, 'parsed', formatAiContent(lastMsg.content || ''))
      }
      this.sending = false
      if (this.activeStreamController) {
        this.activeStreamController = null
      }
    },
    onSend: function () {
      var self = this
      if ((!this.inputText && !this.pendingImage.imageId) || this.sending) return
      var streamController = new AbortController()
      this.activeStreamController = streamController

      var currentImageId = this.pendingImage.imageId
      var currentImageUrl = this.pendingImage.ossUrl || this.pendingImage.url
      var userMsg = {
        id: String(Date.now()) + '-u',
        role: 'user',
        content: this.inputText || (currentImageId ? i18n.t('cs.searchByImage') : ''),
        imageUrl: currentImageUrl || undefined
      }
      this.messages.push(userMsg)
      var aiMsg = {
        id: String(Date.now()) + '-a',
        role: 'assistant',
        content: '',
        stopped: false,
        parsed: null
      }
      this.messages.push(aiMsg)
      this.inputText = ''
      this.pendingImage.url = ''
      this.pendingImage.imageId = ''
      this.pendingImage.ossUrl = ''
      this.sending = true

      this.$nextTick(function () {
        self.autoScroll()
      })

      // 缓冲区 + 定时器节流
      var FLUSH_INTERVAL = 80
      var MAX_FLUSH_SIZE = 4096
      var buffer = ''
      var flushTimer = null
      var aiMsgIndex = self.messages.length - 1

      var flushBuffer = function () {
        if (buffer) {
          if (buffer.length > MAX_FLUSH_SIZE) {
            self.messages[aiMsgIndex].content += buffer.substring(0, MAX_FLUSH_SIZE)
            buffer = buffer.substring(MAX_FLUSH_SIZE)
            flushTimer = setTimeout(flushBuffer, 16)
          } else {
            self.messages[aiMsgIndex].content += buffer
            buffer = ''
            flushTimer = null
          }
        } else {
          flushTimer = null
        }
        var parsed = formatAiContent(self.messages[aiMsgIndex].content)
        self.$set(self.messages[aiMsgIndex], 'parsed', Object.assign({}, parsed, { orders: [], logistics: [], containers: [], capitalRecords: [] }))
        self.autoScroll()
      }

      this.callAiStream(
        userMsg.content,
        function (t) {
          buffer += t
          if (!flushTimer) {
            flushTimer = setTimeout(flushBuffer, FLUSH_INTERVAL)
          }
        },
        streamController.signal,
        currentImageId || undefined,
        currentImageUrl || undefined
      ).then(function () {
        // 流正常结束
      }).catch(function (e) {
        if (e && e.name === 'AbortError') {
          self.messages[aiMsgIndex].stopped = true
          if (!self.messages[aiMsgIndex].content) {
            self.messages[aiMsgIndex].content = i18n.t('cs.generationStopped')
          }
        } else {
          self.messages[aiMsgIndex].content = i18n.t('cs.error') + ': ' + ((e && e.message) || i18n.t('cs.connectionError'))
          console.error('AI Stream Error:', e)
        }
      }).finally(function () {
        if (flushTimer) {
          clearTimeout(flushTimer)
          flushTimer = null
        }
        if (buffer) {
          self.messages[aiMsgIndex].content += buffer
          buffer = ''
        }
        // 最终更新解析结果
        self.$set(self.messages[aiMsgIndex], 'parsed', formatAiContent(self.messages[aiMsgIndex].content))
        if (self.activeStreamController === streamController) {
          self.activeStreamController = null
        }
        self.sending = false
        self.$nextTick(function () {
          self.autoScroll()
          if (self.$refs.textareaRef) {
            self.$refs.textareaRef.focus()
          }
        })
      })
    },

    // === 自动滚动 ===
    autoScroll: function () {
      var self = this
      var scrollEl = this.$refs.scrollEl
      if (!scrollEl) return
      scrollEl.scrollTop = scrollEl.scrollHeight
      this.$nextTick(function () {
        if (!self.$refs.scrollEl) return
        var pres = self.$refs.scrollEl.querySelectorAll('.ws-cs-streaming-pre')
        pres.forEach(function (pre) { pre.scrollTop = pre.scrollHeight })
      })
    },

    // === 物流滑块滚动指示器 ===
    onLogSliderScroll: function (e, logGroup) {
      if (!logGroup || !logGroup.items || logGroup.items.length <= 1) return
      var track = e.target
      var cardWidth = track.querySelector('.cs-logistics-card')
      if (!cardWidth) return
      cardWidth = cardWidth.offsetWidth
      var scrollLeft = track.scrollLeft
      var activeIndex = Math.round(scrollLeft / cardWidth)
      activeIndex = Math.max(0, Math.min(activeIndex, logGroup.items.length - 1))
      // 直接修改 _activeIndex（不需要 $set，因为 parsed 是对象引用）
      if (logGroup._activeIndex !== activeIndex) {
        logGroup._activeIndex = activeIndex
      }
    },
    // === 物流表格滑块滚动 ===
    onLogTableScroll: function (e, logGroup) {
      if (!logGroup || !logGroup.items || logGroup.items.length <= 1) return
      var track = e.target
      var card = track.querySelector('.cs-log-table-card')
      if (!card) return
      var cardWidth = card.offsetWidth + 10
      var scrollLeft = track.scrollLeft
      var activeIndex = Math.round(scrollLeft / cardWidth)
      activeIndex = Math.max(0, Math.min(activeIndex, logGroup.items.length - 1))
      // 直接 DOM 操作 dots，绕过 Vue 深层响应式问题
      var slider = track.parentNode
      var dotsContainer = slider && slider.querySelector('.cs-logistics-dots')
      if (dotsContainer) {
        var dots = dotsContainer.querySelectorAll('.cs-log-dot')
        for (var i = 0; i < dots.length; i++) {
          if (i === activeIndex) {
            dots[i].classList.add('active')
          } else {
            dots[i].classList.remove('active')
          }
        }
      }
    },
    onContainerScroll: function (e, m) {
      var parsed = this.getParsed(m)
      if (!parsed || !parsed.containers || parsed.containers.length <= 1) return
      var track = e.target
      var card = track.querySelector('.cs-container-card')
      if (!card) return
      var cardWidth = card.offsetWidth + 10
      var scrollLeft = track.scrollLeft
      var activeIndex = Math.round(scrollLeft / cardWidth)
      activeIndex = Math.max(0, Math.min(activeIndex, parsed.containers.length - 1))
      var slider = track.parentNode
      var dotsContainer = slider && slider.querySelector('.cs-logistics-dots')
      if (dotsContainer) {
        var dots = dotsContainer.querySelectorAll('.cs-log-dot')
        for (var i = 0; i < dots.length; i++) {
          if (i === activeIndex) {
            dots[i].classList.add('active')
          } else {
            dots[i].classList.remove('active')
          }
        }
      }
    },
    onContainerOrderScroll: function (e, m) {
      var parsed = this.getParsed(m)
      if (!parsed || !parsed.containerOrders || parsed.containerOrders.length <= 1) return
      var track = e.target
      var card = track.querySelector('.cs-container-card')
      if (!card) return
      var cardWidth = card.offsetWidth + 10
      var scrollLeft = track.scrollLeft
      var activeIndex = Math.round(scrollLeft / cardWidth)
      activeIndex = Math.max(0, Math.min(activeIndex, parsed.containerOrders.length - 1))
      var slider = track.parentNode
      var dotsContainer = slider && slider.querySelector('.cs-logistics-dots')
      if (dotsContainer) {
        var dots = dotsContainer.querySelectorAll('.cs-log-dot')
        for (var i = 0; i < dots.length; i++) {
          if (i === activeIndex) {
            dots[i].classList.add('active')
          } else {
            dots[i].classList.remove('active')
          }
        }
      }
    },
    scrollToLogTable: function (logGroup, index) {
      var self = this
      this.$nextTick(function () {
        var tracks = self.$refs.logTableTrack
        if (!tracks) return
        var trackEl = Array.isArray(tracks) ? tracks[0] : tracks
        if (!trackEl) return
        var card = trackEl.querySelector('.cs-log-table-card')
        if (!card) return
        trackEl.scrollTo({ left: index * (card.offsetWidth + 10), behavior: 'smooth' })
      })
    },

    // === 拖拽 ===
    onPointerDown: function (e) {
      e.target.setPointerCapture(e.pointerId)
      this.state.dragging = true
      this.state.dx = e.clientX - this.state.x
      this.state.dy = e.clientY - this.state.y
      window.addEventListener('pointermove', this.onPointerMove, { passive: true })
      window.addEventListener('pointerup', this.onPointerUp, { passive: true })
    },
    onPointerMove: function (e) {
      if (!this.state.dragging) return
      var maxX = window.innerWidth - (this.state.dockedRight ? this.state.size : this.state.w)
      var maxY = window.innerHeight - (this.state.dockedRight ? this.state.size : this.state.h)
      this.state.x = clamp(e.clientX - this.state.dx, 0, maxX)
      this.state.y = clamp(e.clientY - this.state.dy, 0, maxY)
    },
    onPointerUp: function () {
      this.state.dragging = false
      var edgeThreshold = 40
      var rightEdge = window.innerWidth - (this.state.dockedRight ? this.state.size : this.state.w)
      var distToRight = rightEdge - this.state.x
      if (distToRight <= edgeThreshold) {
        this.dockToRight()
      } else {
        this.expandToCenter()
      }
      window.removeEventListener('pointermove', this.onPointerMove)
      window.removeEventListener('pointerup', this.onPointerUp)
    },
    dockToRight: function () {
      this.state.dockedRight = true
      var maxY = window.innerHeight - this.state.size
      var clientWidth = document.documentElement.clientWidth || window.innerWidth
      this.state.x = clientWidth - 44
      this.state.y = clamp(this.state.y, 0, maxY)
    },
    expandToCenter: function () {
      var self = this
      this.state.dockedRight = false
      var maxX = window.innerWidth - this.state.w
      var maxY = window.innerHeight - this.state.h
      if (this.state.x > maxX) this.state.x = Math.max(0, maxX - 10)
      this.state.y = clamp(this.state.y, 0, maxY)
      this.$nextTick(function () {
        self.autoScroll()
        // 首次展开时：如有 sessionId 则加载历史消息，否则加载会话列表取最近会话
        if (!self.messagesLoaded) {
          if (self.sessionId) {
            self.loadSessionMessages(self.sessionId)
          } else {
            // 先加载会话列表，如果有会话则自动加载最近一条
            self.loadSessions()
          }
        }
      })
    },
    onResize: function () {
      var viewport = getViewportSize()
      var clientWidth = viewport.clientWidth
      var clientHeight = viewport.clientHeight
      var panelSize = getResponsivePanelSize()

      this.state.w = panelSize.w
      this.state.h = panelSize.h

      if (this.state.dockedRight) {
        this.state.x = clientWidth - 44
        this.state.y = clamp(this.state.y, 0, clientHeight - this.state.size)
      } else {
        this.state.x = clamp(this.state.x, 0, clientWidth - this.state.w)
        this.state.y = clamp(this.state.y, 0, clientHeight - this.state.h)
      }
    }
  }
}
</script>

<style scoped>
.ws-cs-wrap {
  position: fixed;
  z-index: 9999;
  box-sizing: border-box;
  transition: left .3s ease, top .3s ease, width .3s ease, height .3s ease, transform .3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.ws-cs-wrap.dragging {
  transition: none;
}
.ws-cs-wrap.docked {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: visible;
}
.ws-cs-ball {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 30% 30%, #fffaf5 0%, #ffffff 65%);
  box-shadow: 0 10px 24px rgba(163, 92, 32, 0.18), 0 2px 8px rgba(255, 135, 38, 0.12);
  cursor: pointer;
  user-select: none;
  overflow: hidden;
  border: 1px solid rgba(212, 127, 53, 0.5);
  box-sizing: border-box;
}
.ws-cs-ball-text {
  font-size: 20px;
  font-weight: 700;
  color: #d47f35;
}
.ws-cs-ball-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.ws-cs-avatar-inner {
  background: linear-gradient(135deg, #ff9a46 0%, #f0822b 100%) !important;
}
.ws-cs-avatar-img {
  background: transparent !important;
  overflow: hidden;
}
.ws-cs-avatar-img >>> .el-image {
  width: 100%;
  height: 100%;
}
.ws-cs-avatar-img >>> img {
  object-fit: cover;
}
.avatar-error-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #ff9a46 0%, #f0822b 100%);
  color: #fff;
  font-size: 16px;
}
.ws-cs-panel {
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #fffdfa 0%, #ffffff 14%);
  border-radius: 18px;
  border: 1px solid rgba(212, 127, 53, 0.42);
  box-shadow: 0 18px 44px rgba(36, 24, 12, 0.12), 0 4px 14px rgba(212, 127, 53, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ws-cs-header {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  background: linear-gradient(180deg, rgba(255, 248, 240, 0.96) 0%, rgba(255, 252, 248, 0.98) 55%, #ffffff 100%);
  border-bottom: 1px solid rgba(212, 127, 53, 0.2);
  user-select: none;
  cursor: grab;
  flex-shrink: 0;
}
.ws-cs-header:active {
  cursor: grabbing;
}
.ws-cs-title {
  flex: 1;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #7d4a1f;
  letter-spacing: 0.2px;
}
.ws-cs-header-left {
  flex-shrink: 0;
}
.ws-cs-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 8px;
  gap: 8px;
}
.ws-cs-welcome {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #7d4a1f;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  line-height: 1.6;
  padding: 20px;
  min-height: 0;
}
.ws-cs-list:empty {
  display: none;
}
.ws-cs-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 8px 4px;
  scrollbar-width: thin;
  scrollbar-color: #dcdfe6 transparent;
}
.ws-cs-list::-webkit-scrollbar {
  width: 6px;
}
.ws-cs-list::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 3px;
}
.ws-cs-list::-webkit-scrollbar-track {
  background: transparent;
}
.ws-cs-msg {
  display: flex;
  margin-bottom: 12px;
  width: 100%;
  gap: 8px;
}
.ws-cs-msg.from-user {
  justify-content: flex-end;
}
.ws-cs-msg.from-ai {
  justify-content: flex-start;
  align-items: flex-start;
}
.ws-cs-avatar-wrap {
  flex-shrink: 0;
  margin-top: 2px;
}
.ws-cs-bubble {
  max-width: 80%;
  padding: 8px 10px;
  border-radius: 10px;
  line-height: 1.5;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
}
.from-user .ws-cs-bubble {
  background: #1677ff;
  color: #fff;
}
.from-ai .ws-cs-bubble {
  background: #f5f5f5;
  color: #333;
}
.ws-cs-waiting {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #ff7a45;
  font-size: 13px;
}
.ws-cs-input-area {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid rgba(212, 127, 53, 0.16);
  padding: 10px;
  background: linear-gradient(180deg, rgba(255, 253, 250, 0.96) 0%, rgba(255, 249, 243, 0.92) 100%);
}
.ws-cs-editor {
  border: 1px solid rgba(212, 127, 53, 0.38);
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #fffdfa 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85), 0 4px 10px rgba(212, 127, 53, 0.06);
  padding: 10px 12px 12px;
}
.ws-cs-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.ws-cs-tool-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #c7772f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  font-size: 18px;
}
.ws-cs-tool-btn:hover {
  background: rgba(212, 127, 53, 0.12);
  color: #ad6120;
  transform: translateY(-1px);
}
.ws-cs-tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.ws-cs-count {
  margin-left: auto;
  font-size: 12px;
  color: #b08a68;
}
.ws-cs-textarea >>> .el-textarea__inner {
  height: calc(1.7em * 4) !important;
  min-height: calc(1.7em * 4) !important;
  max-height: calc(1.7em * 4) !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
  resize: none !important;
  overflow-y: auto !important;
  font-size: 14px;
  line-height: 1.7;
  color: #5d4530;
  background: transparent !important;
}
.ws-cs-textarea >>> .el-textarea__inner::placeholder {
  color: #b59a83;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ws-cs-editor-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
.ws-cs-send-btn {
  height: 32px;
  padding: 0 18px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff9a46 0%, #f0822b 100%) !important;
  box-shadow: 0 6px 14px rgba(240, 130, 43, 0.22);
  color: #fff;
  flex-shrink: 0;
}
.ws-cs-stop-btn {
  height: 32px;
  padding: 0 18px;
  border-radius: 999px;
  flex-shrink: 0;
}
.ws-cs-pending-img {
  position: relative;
  padding: 0 0 2px;
}
.pending-thumb {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  border: 1px solid #eee;
}
.pending-remove {
  position: absolute;
  top: -4px;
  left: 52px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0,0,0,0.5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}
.pending-remove:hover {
  background: rgba(0,0,0,0.7);
}
/* 用户消息中的图片 */
.ws-cs-user-img {
  margin-bottom: 4px;
}
.ws-cs-user-thumb {
  width: 120px;
  height: 120px;
  border-radius: 8px;
  cursor: pointer;
}
.ws-cs-theme-default {}

.ws-cs-ai-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
}
.ws-cs-loading-placeholder {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-top: 8px;
  background: #f9f9f9;
  border-radius: 8px;
  color: #ff7a45;
  font-size: 13px;
  width: fit-content;
  border: 1px dashed #ff7a45;
}
.ws-cs-streaming-box {
  margin-top: 6px;
  max-width: 100%;
  overflow: hidden;
}
.ws-cs-streaming-pre {
  background: #f6f8fa;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  padding: 3px 4px;
  font-size: 9px;
  line-height: 1.2;
  color: #aaa;
  overflow-y: auto;
  max-height: 40px;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
  scrollbar-width: thin;
  scrollbar-color: #dcdfe6 transparent;
}
.ws-cs-streaming-pre::-webkit-scrollbar {
  width: 4px;
}
.ws-cs-streaming-pre::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 2px;
}
.ws-cs-streaming-stopped {
  font-size: 12px;
  color: #999;
  padding: 4px 8px;
}
.ws-cs-products-container {
  margin-top: 8px;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 8px;
  scrollbar-width: thin;
  scrollbar-color: #ff7a45 transparent;
}
.ws-cs-products-container::-webkit-scrollbar {
  height: 4px;
  display: block;
}
.ws-cs-products-container::-webkit-scrollbar-thumb {
  background: #ff7a45;
  border-radius: 2px;
}
.ws-cs-products-container::-webkit-scrollbar-track {
  background: #f5f5f5;
}
.ws-cs-order-card {
  flex: 0 0 280px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #eee;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ws-cs-order-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.1);
  border-color: #ff7a45;
}
.ws-cs-order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f5f5f5;
  padding-bottom: 8px;
}
.order-sn {
  font-size: 11px;
  color: #999;
  font-family: monospace;
}
.order-status {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 6px;
  background: #fdfdfd;
  border-radius: 4px;
}
.ws-cs-order-main {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.ws-cs-order-thumb {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  background: #f9f9f9;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
}
.ws-cs-order-thumb >>> .el-image {
  width: 100%;
  height: 100%;
}
.ws-cs-order-title {
  font-size: 13px;
  color: #333;
  font-weight: 500;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  margin-bottom: 4px;
}
.ws-cs-order-brief {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.ws-cs-order-price-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
}
.price-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
}
.price-item .label {
  color: #999;
  width: 32px;
  flex-shrink: 0;
  text-align-last: justify;
}
.price-item .value {
  color: #666;
  font-weight: 500;
}
.price-item .value.highlight {
  color: #ff4d4f;
  font-weight: 700;
}
.ws-cs-order-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: #fafafa;
  padding: 8px 10px;
  border-radius: 8px;
}
.detail-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
}
.detail-item.full-width {
  width: 100%;
}
.detail-item.small-padding {
  padding-top: 2px;
}
.detail-item.full-width.no-border {
  border: none;
  padding-top: 0;
  margin-top: 0;
}
.cs-order-time-value {
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
.detail-item .label {
  color: #999;
  width: 60px;
  flex-shrink: 0;
  white-space: nowrap;
  text-align-last: justify;
}
.detail-item .value {
  color: #666;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ws-cs-order-footer {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-top: 1px dashed #eee;
  padding-top: 8px;
}
.footer-tag {
  font-size: 11px;
  color: #52c41a;
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f6ffed;
  padding: 2px 8px;
  border-radius: 4px;
  width: fit-content;
}
.footer-remark {
  font-size: 11px;
  color: #fa8c16;
  background: #fff7e6;
  padding: 4px 8px;
  border-radius: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 24px;
}
.image-slot-small {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 16px;
}
/* ========== 订单详情样式（order_detail） ========== */
.cs-detail-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 2px;
}
.cs-detail-id-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #fff7ed, #ffedd5);
  border-radius: 8px;
  border: 1px solid #fed7aa;
}
.cs-detail-status {
  font-weight: 600;
  font-size: 13px;
}
/* 时间/数量网格 */
.cs-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px 8px;
  background: #fafafa;
  border: 1px solid #f0e8dc;
  border-radius: 8px;
  padding: 10px 10px 4px;
}
.grid-item {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding-bottom: 8px;
  font-size: 13px;
}
.grid-full {
  grid-column: span 3;
}
.grid-two-third {
  grid-column: span 2;
}
.grid-item .gl {
  color: #999;
  white-space: nowrap;
  flex-shrink: 0;
}
.grid-item .gv {
  color: #333;
  font-weight: 500;
}
/* 商品区域 */
.cs-detail-goods {
  display: flex;
  gap: 10px;
  padding: 10px;
  border: 1px solid #f0e8dc;
  border-radius: 8px;
  background: #fff;
}
.cs-detail-thumb {
  width: 72px;
  height: 72px;
  border-radius: 6px;
  overflow: hidden;
  background: #f9f9f9;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
}
.cs-detail-goods-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.cs-goods-name {
  font-size: 12px;
  line-height: 1.5;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-all;
}
.cs-goods-spec {
  font-size: 11px;
  color: #666;
  margin-top: 4px;
}
.cs-spec-val:empty::after,
.cs-goods-spec .cs-spec-val {
  color: inherit;
}
.cs-goods-spec .cs-spec-val:first-child:not(:only-child) {
  margin-right: 8px;
}
.cs-goods-price-row {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}
.price-red {
  color: #ff4d4f;
  font-weight: 700;
}
/* 物流/地址信息网格 */
.cs-detail-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
  padding: 10px;
  border: 1px solid #f0e8dc;
  border-radius: 8px;
  background: #fff;
}
.info-half, .info-full {
  display: flex;
  gap: 4px;
  font-size: 11px;
  line-height: 1.7;
}
.info-full {
  grid-column: span 2;
}
.info-half .il, .info-full .il {
  color: #999;
  white-space: nowrap;
  flex-shrink: 0;
}
.info-half .iv, .info-full .iv {
  color: #333;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.remark-text {
  color: #fa8c16 !important;
}
.info-last .iv {
  white-space: normal;
}
/* 费用汇总表格 */
.cs-detail-price-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  border: 1px solid #f0e8dc;
  border-radius: 8px;
  overflow: hidden;
}
.cs-detail-price-table td {
  padding: 6px 8px;
  text-align: left;
  border: none;
}
.cs-detail-price-table tr:nth-child(odd) td:nth-child(odd),
.cs-detail-price-table tr:nth-child(odd) td:nth-child(even) {
  background: #fbfdff;
}
.cs-detail-price-table tr:nth-child(even) td:nth-child(odd),
.cs-detail-price-table tr:nth-child(even) td:nth-child(even) {
  background: #fff;
}
.cs-detail-price-table tr:nth-child(odd) td:first-child,
.cs-detail-price-table tr:nth-child(even) td:nth-child(3) {
  width: 30%;
  color: #333;
}
.cs-detail-price-table tr:nth-child(odd) td:nth-child(2),
.cs-detail-price-table tr:nth-child(even) td:nth-child(4) {
  width: 20%;
  color: #333;
  font-weight: 600;
}
.row-total td {
  background: #fbfdff;
  font-weight: 700;
}
.total-val {
  color: #ff4d4f !important;
  font-size: 13px;
}
.fee-tip {
  color: #5e5eee;
  cursor: help;
  margin-left: 2px;
}
/* ========== 会话列表覆盖层 ========== */
.ws-cs-session-overlay {
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fffdfa;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ws-cs-session-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(212, 127, 53, 0.16);
  flex-shrink: 0;
}
.ws-cs-session-title {
  font-size: 14px;
  font-weight: 600;
  color: #7d4a1f;
}
.ws-cs-session-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  scrollbar-width: thin;
  scrollbar-color: #dcdfe6 transparent;
}
.ws-cs-session-list::-webkit-scrollbar {
  width: 6px;
}
.ws-cs-session-list::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 3px;
}
.ws-cs-session-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s ease;
  border: 1px solid transparent;
  margin-bottom: 6px;
}
.ws-cs-session-item:hover {
  background: #fff7ed;
  border-color: rgba(212, 127, 53, 0.2);
}
.ws-cs-session-item.active {
  background: #fff1e0;
  border-color: rgba(212, 127, 53, 0.4);
}
.ws-cs-session-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}
.ws-cs-session-last {
  font-size: 13px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
}
.ws-cs-session-time {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}
.ws-cs-session-loading,
.ws-cs-session-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 40px 0;
  color: #999;
  font-size: 13px;
}
.ws-cs-session-loadmore {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  color: #ff7a45;
  font-size: 16px;
}
/* ========== 物流轨迹样式（logistics） ========== */
.ws-cs-logistics-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 2px;
}
.cs-logistics-summary {
  border: 1px solid #f0e8dc;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
}
.cs-logistics-state-text {
  color: #ff7a45 !important;
  font-weight: 600;
}
.cs-logistics-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #7d4a1f;
  padding: 6px 10px;
  background: #fff7ed;
  border-radius: 6px 6px 0 0;
  border: 1px solid #f0e8dc;
  border-bottom: none;
}
.cs-logistics-card-title + .cs-logistics-summary {
  border-radius: 0 0 8px 8px;
}
.cs-logistics-card-title + .cs-logistics-empty {
  border-radius: 0 0 8px 8px;
}
.cs-logistics-empty {
  padding: 16px 12px;
  text-align: center;
  color: #999;
  font-size: 13px;
  background: #fafafa;
  border: 1px solid #f0e8dc;
  border-radius: 8px;
}
.cs-logistics-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
}
/* 物流时间线 */
.cs-logistics-timeline {
  border: 1px solid #f0e8dc;
  border-radius: 8px;
  padding: 12px 10px 10px 20px;
  background: #fff;
  position: relative;
}
.cs-timeline-item {
  position: relative;
  padding-left: 20px;
  padding-bottom: 14px;
}
.cs-timeline-item:last-child {
  padding-bottom: 0;
}
.cs-timeline-dot {
  position: absolute;
  left: 0;
  top: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d9d9d9;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #d9d9d9;
  z-index: 1;
}
.cs-timeline-dot.active {
  background: #ff7a45;
  box-shadow: 0 0 0 1px #ff7a45;
}
.cs-timeline-line {
  position: absolute;
  left: 4px;
  top: 16px;
  width: 2px;
  bottom: 0;
  background: #e8e8e8;
}
.cs-timeline-content {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 8px;
  align-items: baseline;
}
.cs-timeline-date {
  font-size: 11px;
  color: #999;
  white-space: nowrap;
}
.cs-timeline-location {
  font-size: 12px;
  color: #333;
  font-weight: 500;
}
.cs-timeline-detail {
  font-size: 11px;
  color: #666;
}
/* ========== 订单列表信息行 ========== */
.cs-list-info-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 13px;
  color: #666;
  padding: 2px 4px;
}
.cs-list-info-row .gl {
  color: #999;
  white-space: nowrap;
  flex-shrink: 0;
}
.cs-list-info-row .gv {
  color: #333;
  font-weight: 500;
}
/* ========== 订单列表滑块卡片 ========== */
.cs-order-detail-card {
  flex: 0 0 300px;
  min-width: 280px;
  max-width: 340px;
  background: #fff;
  border: 1px solid #f0e8dc;
  border-radius: 12px;
  padding: 14px;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 2px 8px rgba(212, 127, 53, 0.06);
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
}
.cs-order-detail-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.1);
  border-color: #ff7a45;
}

/* ========== 批次列表滑块卡片 ========== */
.cs-container-slider {
  width: 100%;
  overflow: hidden;
}
.cs-container-track {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 4px;
}
.cs-container-track::-webkit-scrollbar {
  display: none;
}
.cs-container-card {
  flex: 0 0 340px;
  min-width: 320px;
  max-width: 380px;
  background: #fff;
  border: 1px solid #f0e8dc;
  border-radius: 12px;
  padding: 16px;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 2px 12px rgba(212, 127, 53, 0.08);
  transition: all 0.3s ease;
}
.cs-container-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.1);
  border-color: #ff7a45;
}
.cs-container-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 12px;
  background: #fafafa;
  border: 1px solid #f0e8dc;
  border-radius: 10px;
  padding: 12px 14px 6px;
}
/* ========== 物流滑块卡片 ========== */
.cs-logistics-slider {
  width: 100%;
  overflow: hidden;
}
.cs-logistics-track {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 4px;
}
.cs-logistics-track::-webkit-scrollbar {
  display: none;
}
.cs-logistics-card {
  flex: 0 0 calc(100% - 14px);
  min-width: 240px;
  max-width: 340px;
  background: #fff;
  border: 1px solid #f0e8dc;
  border-radius: 12px;
  padding: 14px 16px;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 2px 8px rgba(212, 127, 53, 0.06);
}
.cs-log-card-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.cs-log-card-row i {
  font-size: 15px;
  color: #ff7a45;
  flex-shrink: 0;
}
.cs-log-card-batch span {
  color: #333;
  font-weight: 600;
  word-break: break-all;
}
.cs-log-card-tracking span {
  font-family: monospace;
  color: #555;
  letter-spacing: 0.3px;
  word-break: break-all;
}
.cs-log-card-status {
  background: #fbfdff;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cs-log-step {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
}
.cs-log-step-time {
  color: #999;
}
.cs-log-step-desc {
  color: #333;
  word-break: break-word;
}
/* 滑动指示器 */
.cs-logistics-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
}
.cs-log-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #dcdfe6;
  transition: all 0.25s ease;
}
.cs-log-dot.active {
  background: #ff7a45;
  transform: scale(1.35);
}
/* ========== 物流表格滑块 ========== */
.cs-log-table-slider {
  width: 100%;
  overflow: hidden;
}
.cs-log-table-track {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 4px;
}
.cs-log-table-track::-webkit-scrollbar {
  display: none;
}
.cs-log-table-card {
  flex: 0 0 calc(100% - 14px);
  min-width: 260px;
  background: #fff;
  border: 1px solid #f0e8dc;
  border-radius: 12px;
  padding: 10px 8px;
  scroll-snap-align: start;
  box-shadow: 0 2px 8px rgba(212, 127, 53, 0.06);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
/* ========== 物流表格（精确复刻 logisticsInfo.vue .table-content） ========== */
.cs-logistics-table-wrap {
  margin-bottom: 12px;
}
.cs-log-table {
  width: 100%;
  height: auto;
  border-collapse: collapse;
}
.cs-log-table thead {
  background-color: #3a4255;
  color: #ffffff;
  text-align: left;
}
.cs-log-table thead th {
  padding: 12px 20px;
}
.cs-log-table tbody {
  background-color: #ffffff;
}
.cs-log-table tbody td {
  font-size: 14px;
  color: #3a4255;
  padding: 12px 20px;
  border: 1px solid #e5e5eb;
  vertical-align: top;
  line-height: 1.6;
}
.cs-log-table .cs-log-td-bg {
  background-color: #f5f6f8;
}
/* 轨迹三列强制分列 */
.cs-log-col-status {
  width: 35%;
  word-break: break-word;
}
.cs-log-col-time {
  width: 32%;
  white-space: nowrap;
}
.cs-log-col-location {
  width: 33%;
  word-break: break-word;
}
.cs-log-btn-green {
  display: inline-block;
  background-color: #2e85a1;
  border-radius: 4px;
  padding: 7px 23px;
  color: #ffffff;
}
.cs-log-text-green {
  color: #2e85a1;
  font-size: 18px;
}
/* ========== 商品推荐滑块 ========== */
.ws-cs-products-scroll {
  display: flex;
  gap: 8px;
  padding: 0 4px;
}
.ws-cs-product-card {
  flex: 0 0 130px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #eee;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}
.ws-cs-product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-color: #ff7a45;
}
.ws-cs-product-image {
  width: 130px;
  height: 130px;
  background: #f9f9f9;
}
.ws-cs-product-image .el-image {
  width: 100%;
  height: 100%;
}
.ws-cs-product-info {
  padding: 8px;
}
.ws-cs-product-name {
  font-size: 12px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
  height: 2.8em;
  font-weight: 500;
}
.ws-cs-product-price {
  font-size: 15px;
  color: #ff4d4f;
  font-weight: 700;
  margin-top: 6px;
}
.ws-cs-product-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}
.platform-tag {
  font-size: 10px;
  background: #fff2e8;
  color: #fa541c;
  padding: 1px 4px;
  border-radius: 4px;
  border: 0.5px solid #ffbb96;
}
.sold-count {
  font-size: 10px;
  color: #999;
}
/* ========== 以图搜图相关样式 ========== */
.ws-cs-pending-img {
  position: relative;
  padding: 0 0 2px;
}
.pending-thumb {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  border: 1px solid #eee;
}
.pending-remove {
  position: absolute;
  top: -4px;
  left: 52px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0,0,0,0.5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}
.pending-remove:hover {
  background: rgba(0,0,0,0.7);
}
.ws-cs-tool-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #c7772f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}
.ws-cs-tool-btn:hover {
  background: rgba(212, 127, 53, 0.12);
  color: #ad6120;
  transform: translateY(-1px);
}
.ws-cs-tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.ws-cs-tool-btn i {
  font-size: 18px;
}
.ws-cs-user-img {
  margin-bottom: 4px;
}
.ws-cs-user-thumb {
  width: 120px;
  height: 120px;
  border-radius: 8px;
  cursor: pointer;
}
.ws-cs-user-thumb .el-image {
  width: 100%;
  height: 100%;
}
</style>